import { useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import { PREVIEW_NO_LAYOUT_SOURCE, COMPONENT_IDENTIFIER_KEYWORD, generateProps } from '../../lib';
import { useIframeContentSync } from '../../hooks/useIframeContentSync';
import { useDynamicEntityStoreContext } from '../../contexts';

function ComponentPreview({ component }) {
  const iFrameRef = useRef(null);

  const viewport = useDynamicEntityStoreContext((state) => state.viewport);

  const { ref, inView } = useInView({
    threshold: 0.3,
  });

  const componentForPreview = useMemo(() => {
    const props = generateProps(component);
    const { propSchema, component_category, label, ...rest } = component;

    return {
      id: crypto.randomUUID(),
      ...(Object.keys(props).length > 0 && { props }),
      ...rest,
    };
  }, []);

  const { handleIframeLoad } = useIframeContentSync({
    iFrameRef,
    content: [componentForPreview],
  });

  const viewportClass = useMemo(() => {
    switch (viewport) {
      case 2:
        return 'tablet';
      case 3:
        return 'mobile';
      default:
        return 'desktop';
    }
  }, [viewport]);

  const handleOnLoad = () => {
    if (iFrameRef.current?.contentWindow) {
      // This will suppress all console messages (log, warn, error, etc.) from the iframe.
      const iframeConsole = iFrameRef.current.contentWindow.console;
      Object.keys(iframeConsole).forEach((key) => {
        if (typeof iframeConsole[key] === 'function') {
          // eslint-disable-next-line no-console
          iframeConsole[key] = () => {};
        }
      });
    }

    handleIframeLoad();
  };

  return (
    <div ref={ref} className="preview-div">
      {inView && (
        <iframe
          sandbox="allow-scripts allow-same-origin allow-forms"
          className={viewportClass}
          ref={iFrameRef}
          onLoad={handleOnLoad}
          src={`/${PREVIEW_NO_LAYOUT_SOURCE}`}
          title={`Preview of ${component[COMPONENT_IDENTIFIER_KEYWORD]}`}
        />
      )}
    </div>
  );
}

export default ComponentPreview;
